import { type Dispatch, type SetStateAction, lazy } from "react"

import type { UseMutationResult } from "react-query"
import type { Edge } from "reactflow"

import type { AxiosError, AxiosResponse } from "axios"

import type { CalculatorGroupRetrieve } from "../../types/generated/api/CalculatorGroupRetrieve"
import type { CalculatorList } from "../../types/generated/api/CalculatorList"
import type { ComponentTest as ComponentTestResponse } from "../../types/generated/api/ComponentTest"
import type { KonanBasePredictionResponse } from "../../types/generated/api/KonanBasePredictionResponse"
import type { Program } from "../../types/generated/api/Program"
import type { RuleSet } from "../../types/generated/api/RuleSet"
import type { RuleSetGroupRetrieve } from "../../types/generated/api/RuleSetGroupRetrieve"
import type { RuleSetList } from "../../types/generated/api/RuleSetList"
import type { ScoreCardGroupRetrieve } from "../../types/generated/api/ScoreCardGroupRetrieve"
import type { ScoreCardSetList } from "../../types/generated/api/ScoreCardSetList"
import type { TagListGroupRetrieve } from "../../types/generated/api/TagListGroupRetrieve"
import type { TagListList } from "../../types/generated/api/TagListList"
import type { WorkflowInputFeature } from "../../types/generated/api/WorkflowInputFeature"
import type { WorkflowOutputResponse } from "../../types/generated/api/WorkflowOutputResponse"
import { NodeSimpleChip } from "../Workflows"

const CustomEdgeLabel = lazy(() =>
  import("../Workflows/components/canvas/CustomEdgeLabel").then((module) => ({
    default: module.CustomEdgeLabel,
  })),
)

// Define constants for editor types to ensure type safety and prevent typos
export const EDITOR_TYPES = {
  JSON: "json",
  TABLE: "table",
} as const

// Create a type from the EDITOR_TYPES constant
export type EditorType = (typeof EDITOR_TYPES)[keyof typeof EDITOR_TYPES]

// From PredictionCreationFlow.tsx
export type DialogProps = {
  isOpen: boolean
  isTestModal: boolean
  onClose: () => void
}

// supported components for testing
export const COMPONENT_TYPES = {
  RULESET: "ruleset",
  SCORECARDSET: "scorecard_set",
  SCORECARD: "scorecard",
  SCORETABLE: "scoretable",
  TAGLIST: "taglist",
  CALCULATOR: "calculator",
  PROGRAM: "program",
} as const

export type PredictionType = "manual" | "bulk" | "component" | null

export type ModelPredicitonsTestMutation = {
  prediction_uuid: string
  output: {
    additionalProp1: string
    additionalProp2: string
    additionalProp3: string
  }
  workflow_output: WorkflowOutputResponse
  joint_output: string
}

export type WorkflowPredictionsTestMutation = {
  satisfied_flow_path: string
  output_label: string
  description: string
  status: string
  calculated_features: {
    additionalProp1: string
    additionalProp2: string
    additionalProp3: string
  }
  reason: string
  last_node: string
}

export type SelectedComponent = {
  id: string
  type: (typeof COMPONENT_TYPES)[keyof typeof COMPONENT_TYPES]
  data:
    | RuleSet
    | RuleSetList
    | RuleSetGroupRetrieve
    | ScoreCardSetList
    | Program
    | TagListList
    | TagListGroupRetrieve
    | CalculatorList
    | CalculatorGroupRetrieve
    | ScoreCardGroupRetrieve
}
// From Context.ts
export type PredictionContextType = {
  type: PredictionType
  setType?: Dispatch<SetStateAction<PredictionType>>
  step: number
  setStep?: Dispatch<SetStateAction<number>>
  isSendingBulkRequests: boolean
  setIsSendingBulkRequests?: Dispatch<SetStateAction<boolean>>
  sendPredictionMutation?: UseMutationResult<
    AxiosResponse<KonanBasePredictionResponse>,
    AxiosError,
    {
      projectId: string
      predictionBody?: Record<PropertyKey, unknown>
    },
    unknown
  >
  sendModelPredictionTestMutation?: UseMutationResult<
    AxiosResponse,
    AxiosError,
    {
      modelId: string
      predictionBody: Record<PropertyKey, unknown>
    },
    ModelPredicitonsTestMutation
  >
  sendWorkFlowPredictionTestMutation?: UseMutationResult<
    AxiosResponse,
    AxiosError,
    {
      workflowId: string
      predictionBody: Record<PropertyKey, unknown>
    },
    WorkflowPredictionsTestMutation
  >
  isTestModal: boolean
}

// From SinglePredictionCreation.tsx and TabularEditor.tsx
export type SchemaFeature = {
  name: string
  type: WorkflowInputFeature.type
  id: string
  value: unknown
  new?: boolean
}

export type Schema = Record<string, SchemaFeature>

// From TabularEditor.tsx
export interface TableFormEditorProps {
  schema: Schema
  setSchema: Dispatch<SetStateAction<Schema>>
  rowsPerPage: number
  page: number
  search?: string
}

// From SinglePredictionCreation.tsx
export interface PredictionOutputProps {
  data: KonanBasePredictionResponse | WorkflowOutputResponse
  isExpanded: boolean
  setIsExpanded: Dispatch<SetStateAction<boolean>>
  isTestData: boolean
}

// From ComponentTestingCreation.tsx
export interface TestingOutputProps {
  data: ComponentTestResponse
  isExpanded: boolean
  setIsExpanded: Dispatch<SetStateAction<boolean>>
}

export type BulkCreateType = {
  onClose: () => void
}

export type SchemaTypeIconType = {
  type: WorkflowInputFeature.type
}

export interface TestMutationParams {
  projectId: string
  componentId: string
  componentType: "rulesets" | "scorecards" | "scorecardsets" | "programs" | "taglists" | "calculators"
  body: Record<PropertyKey, unknown>
}

// component testing Canvas Types
export interface ComponentTestingCanvasProps {
  selectedComponent: SelectedComponent | null
  setSelectedComponent: Dispatch<SetStateAction<SelectedComponent | null>>
}

export const nodeTypes = {
  StartNode: NodeSimpleChip,
  ScriptNode: NodeSimpleChip,
  CalculatorNode: NodeSimpleChip,
  LabeledNode: NodeSimpleChip,
  EndResultNode: NodeSimpleChip,
  FilterNode: NodeSimpleChip,
  LoopStartNode: NodeSimpleChip,
  LoopEndNode: NodeSimpleChip,
  TaglistNode: NodeSimpleChip,
  ProgramNode: NodeSimpleChip,
  ProjectNode: NodeSimpleChip,
  RulesetNode: NodeSimpleChip,
  ScorecardsetNode: NodeSimpleChip,
}

export const edgeTypes = {
  customLabel: CustomEdgeLabel,
  custom: CustomEdgeLabel,
}

type ComponentTestingEdgeData = {
  text?: string
  useCustomLabel?: boolean
  labelType?: string
  edgeType?: string
}

export type ComponentTestingEdge = Edge<ComponentTestingEdgeData>
